import { Fragment, useState, useEffect } from "react";
import { useRouteLoaderData } from "react-router-dom";
import ModalNew from "./Modal";
import PopupHeader from "./common/popupHeader";
import PopupContent from "./common/PopupContent";
import PopupSection from "./common/PopupSection";
import ApiResponseModal from "./ApiResponseModal";
import { sendReferralInvitations } from "../../../api/referral/referralAPI";
import SmallAlert from "../alerts/SmallAlert";
import getAlertTitle from "../../../utils/getAlertTitle";
import classes from "./Modal.module.css";

function SharePopUp({ setIsModalOpen }) {
  const [selectedService, setSelectedService] = useState("");
  const [emailList, setEmailList] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alertType, setAlertType] = useState(null);
  const [message, setMessage] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(5);

  // ApiResponseModal states
  const [isResultModalOpen, setIsResultModalOpen] = useState(false);
  const [isResultLoading, setIsResultLoading] = useState(false);
  const [isResultSuccess, setIsResultSuccess] = useState(false);
  const [resultMessage, setResultMessage] = useState("");

  const values = useRouteLoaderData("root");
  const token = values?.token;

  const disabled = emailSent || isSubmitting;

  useEffect(() => {
    let timer;
    if (emailSent && secondsLeft > 0) {
      timer = setInterval(() => {
        setSecondsLeft((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [emailSent, secondsLeft]);

  const handleSubmitEmailReferral = async (e) => {
    e.preventDefault();

    if (!selectedService) {
      setAlertType("error");
      setMessage("Please select a service first");
      return;
    }

    if (!emailList.trim()) {
      setAlertType("error");
      setMessage("Please enter at least one email address");
      return;
    }

    if (!token) {
      setAlertType("error");
      setMessage("You must be logged in to send referral invitations");
      return;
    }

    setIsSubmitting(true);
    setAlertType(null);
    setMessage("");

    // Open result modal and show loading
    setIsResultModalOpen(true);
    setIsResultLoading(true);
    setIsResultSuccess(false);

    try {
      const apiResponse = await sendReferralInvitations({
        service: selectedService,
        emails: emailList,
        share: true,
      });

      const data = await apiResponse.json();

      if (apiResponse.ok) {
        if (
          data.invalid_emails &&
          Array.isArray(data.invalid_emails) &&
          data.invalid_emails.length > 0
        ) {
          setIsResultSuccess(false);
          setResultMessage(data.message);
        } else {
          setIsResultSuccess(true);
          setResultMessage(
            "Your referral invitations have been sent successfully!"
          );
          setEmailList("");

          setTimeout(() => {
            handleClose();
          }, 5000);
        }
      } else {
        setIsResultSuccess(false);
        setResultMessage(
          data.message ||
            "Failed to send referral invitations. Please try again."
        );
      }
    } catch (error) {
      setIsResultSuccess(false);
      setResultMessage(error.message || "An error occurred. Please try again.");
      console.error("Error sending referral invitations:", error);
    } finally {
      setIsSubmitting(false);
      setIsResultLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedService("");
    setEmailList("");
    setIsSubmitting(false);
    setAlertType(null);
    setMessage("");
    setEmailSent(false);
    setSecondsLeft(5);
    setIsModalOpen(false);
  };

  const handleCloseResultModal = () => {
    setIsResultModalOpen(false);
    if (isResultSuccess) {
      handleClose(); // Close the main modal on success
    }
  };

  const handleRetry = () => {
    setIsResultModalOpen(false);
    // Reset form for retry
    setAlertType(null);
    setMessage("");
  };

  return (
    <Fragment>
      {!isResultModalOpen && (
        <ModalNew className="content-modal">
          <PopupHeader
            title="Share the Help — Refer a Friend"
            setOpenState={setIsModalOpen}
            icon={`${process.env.PUBLIC_URL}/images/icons/friend_blue_popup.svg`}
            variant="compact"
          />

          <PopupContent>
            <PopupSection>
              Know someone who could use expert tax help? Send them your Plaintr
              link — it only takes a minute.
            </PopupSection>

            {alertType && (
              <div className="alert-container" style={{ marginBottom: "20px" }}>
                <SmallAlert
                  alert={true}
                  title={getAlertTitle(alertType)}
                  type={alertType}
                  message={message}
                  dismissOnClickOutside={true}
                  dismissOnFocusOutside={false}
                />
              </div>
            )}

            {!emailSent && (
              <div className={classes.modalFormSection}>
                <div className="form-field">
                  <select
                    name="referralService"
                    value={selectedService}
                    onChange={(e) => setSelectedService(e.target.value)}
                    disabled={disabled}
                    className="form-input"
                    required
                  >
                    <option value="" disabled>
                      Select a service to refer...
                    </option>
                    <option value="TF">Tax Filing</option>
                  </select>
                </div>

                <div className="form-field">
                  <textarea
                    id="emailList"
                    name="emailList"
                    value={emailList}
                    onChange={(e) => setEmailList(e.target.value)}
                    required
                    rows="3"
                    className="form-textarea"
                    disabled={disabled}
                    maxLength={1000}
                    placeholder="Email Addresses (comma separated)"
                  />
                  <small className="character-count">
                    {emailList.length}/1000 characters
                  </small>
                </div>

                <button
                  type="button"
                  onClick={handleSubmitEmailReferral}
                  className={`cta-button primary ${disabled ? "disabled" : ""}`}
                  data-icon={isSubmitting ? "hourglass_empty" : "send"}
                  disabled={disabled}
                >
                  <b>{isSubmitting ? "Sending..." : "Send Invite"}</b>
                </button>
              </div>
            )}
          </PopupContent>
        </ModalNew>
      )}

      {/* ApiResponseModal */}
      <ApiResponseModal
        isOpen={isResultModalOpen}
        isLoading={isResultLoading}
        isSuccess={isResultSuccess}
        message={resultMessage}
        onClose={handleCloseResultModal}
        onRetry={handleRetry}
      />
    </Fragment>
  );
}

export default SharePopUp;
